import { MultiSelectOption } from "@dune/types";
import { Loader2 } from "lucide-react";
import * as React from "react";
import { cn } from "../../lib/utils";
import { MultipleSelector } from "../ui/multiselect";

interface CustomEmailFieldProps {
  id?: string;
  label?: string;
  placeholder?: string;
  value: MultiSelectOption[];
  onValueChange: (value: MultiSelectOption[]) => void;
  onSearch: (value: string) => Promise<MultiSelectOption[]>;
  disabled?: boolean;
  className?: string;
  size?: "medium" | "large";
  creatable?: boolean;
  isSearching?: boolean;
  defaultOptions?: MultiSelectOption[];
  triggerSearchOnFocus?: boolean;
  delay?: number;
}

export const CustomEmailField = React.forwardRef<
  HTMLDivElement,
  CustomEmailFieldProps
>(
  (
    {
      id,
      label,
      placeholder,
      value,
      onValueChange,
      onSearch,
      disabled = false,
      className,
      size = "medium",
      creatable = true,
      isSearching = false,
      defaultOptions = [],
      triggerSearchOnFocus = false,
      delay = 200,
    },
    ref,
  ) => {
    const labelId = id ? `${id}-label` : `email-field-label-${React.useId()}`;
    const inputRef = React.useRef<HTMLDivElement>(null);

    const focusInput = () => {
      const input = inputRef.current?.querySelector("input");
      if (input) {
        input.focus();
      }
    };

    return (
      <div className={cn("w-full", className)} ref={ref}>
        {label && (
          <div
            id={labelId}
            className="text-sm font-medium block text-foreground mb-1"
          >
            <button
              type="button"
              onClick={focusInput}
              className="bg-transparent border-0 p-0 text-inherit cursor-pointer font-inherit text-left w-full"
              disabled={disabled}
              tabIndex={-1}
            >
              {label}
            </button>
          </div>
        )}
        <div
          ref={inputRef}
          className={cn(
            "relative w-full",
            "before:absolute before:bottom-0 before:left-0 before:h-px before:w-full before:bg-border",
            "after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-primary after:transition-all after:duration-200 after:ease-out focus-within:after:w-full",
          )}
          aria-labelledby={label ? labelId : undefined}
        >
          <div className="relative">
            <MultipleSelector
              value={value}
              placeholder={placeholder}
              onChange={onValueChange}
              delay={delay}
              disabled={disabled}
              creatable={creatable}
              onSearch={onSearch}
              defaultOptions={defaultOptions}
              triggerSearchOnFocus={triggerSearchOnFocus}
              className={cn(
                "border-none shadow-none rounded-none",
                "focus-within:border-none focus-within:ring-0",
                "before:hidden after:hidden",
                size === "large" ? "text-lg" : "text-base",
                disabled && "bg-transparent",
                isSearching && "pr-8",
              )}
              badgeClassName={cn(
                "bg-primary/10 text-primary border-none",
                "hover:bg-primary/15",
                "transition-colors duration-150",
              )}
            />
            {isSearching && (
              <div className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                <Loader2 className="h-6 w-6 text-primary animate-spin" />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

CustomEmailField.displayName = "CustomEmailField";

export default CustomEmailField;
